import React, { useMemo, useState } from 'react';
import { Coords } from '@energy-stacks/fleet/shared';
import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import {
  IconX,
  IconClockHour5,
  IconChevronLeft,
  IconChevronRight,
  IconArrowRightRhombus,
  IconArrowLeftRhombus,
  IconMapRoute,
} from '@tabler/icons-react';
import { EditingTourJob } from '../edit-tour/editingTourJobModel';
import { formatDate } from '@energy-stacks/shared';
import { formatDuration } from '@energy-stacks/core/ui';
import { JobLocationIcon } from '@energy-stacks/fleet/feature-jobs';
import { useTranslation } from 'react-i18next';
import { useRemovePlantIdPrefix } from '@energy-stacks/fleet/feature-business-accounts';
import { RawJobIcon } from './RawJobIcon';
import { DegassedJobIcon } from './DegassedJobIcon';
import { useUpdateEffect } from 'usehooks-ts';

type JobMapPopupCardProps = Coords & {
  jobs: EditingTourJob[];
  jobPool?: EditingTourJob[];
  zIndex: number;
  onAddToTour?: (job: EditingTourJob) => void;
  onClose: () => void;
  clusterId: number | undefined;
  onMouseOver: () => void;
  onMouseLeave: () => void;
  onShowOnMap: (clusterId: number) => void;
};

export const JOB_MAP_POPUP_WIDTH = 340;

export const JobMapPopupCard: React.FC<JobMapPopupCardProps> = ({
  jobs,
  jobPool = [],
  onAddToTour,
  onClose,
  onShowOnMap,
  clusterId,
  onMouseOver,
  onMouseLeave,
}) => {
  const [t] = useTranslation('tours');
  const [selectedIndex, setSelectedIndex] = useState(0);

  const selectedJob = jobs[selectedIndex];
  const removePlantIdPrefix = useRemovePlantIdPrefix();

  const isJobFromPool = useMemo(() => {
    if (!selectedJob) {
      return false;
    }
    return jobPool.find((job) => job.jobId === selectedJob.jobId);
  }, [jobPool, selectedJob]);

  useUpdateEffect(() => {
    // Since we are rendering one JobMapPopupCard instance over the whole map
    // selectedIndex needs to reset when clusterId changes (different cluster/marker clicked)
    setSelectedIndex(0);
  }, [clusterId]);

  if (!selectedJob) {
    return null;
  }

  return (
    <div onMouseOver={onMouseOver} onMouseLeave={onMouseLeave}>
      <Stack
        justifyContent="center"
        width={JOB_MAP_POPUP_WIDTH}
        p={3}
        sx={{ backgroundColor: 'background.paper', borderRadius: 4 }}
        gap={2.5}
      >
        {/*
          MARK: Header
        */}
        <Stack
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          <Stack direction="row" gap={3} alignItems="center">
            <Typography fontSize={14} color="#1A1A1A">
              {selectedIndex + 1}/{jobs.length}
            </Typography>
            {selectedJob.product?.name.includes('raw') ? (
              <RawJobIcon size={15} />
            ) : (
              <DegassedJobIcon size={15} />
            )}
            <Typography fontSize={14} fontWeight={700} color="#1A1A1A">
              {removePlantIdPrefix(selectedJob.jobId)}
            </Typography>
          </Stack>
          <IconButton size="small" onClick={onClose}>
            <IconX color="#1A1A1A" width={20} height={20} stroke={1.5} />
          </IconButton>
        </Stack>
        {/*
          MARK: Job info
        */}
        <Stack width="100%" gap={2}>
          <JobInfoRow
            label={'Quantity'}
            value={`${selectedJob.jobType} (${selectedJob.product?.quantity})`}
            icon={
              selectedJob.jobType === 'INBOUND' ? (
                <IconArrowRightRhombus stroke={1.5} size={20} color="#1A1A1A" />
              ) : (
                <IconArrowLeftRhombus stroke={1.5} size={20} color="#1A1A1A" />
              )
            }
          />
          <JobInfoRow
            label={'Tank'}
            value={
              selectedJob.origin?.id
                ? removePlantIdPrefix(selectedJob.origin.id)
                : ''
            }
            icon={
              selectedJob.origin && (
                <Box sx={{ mr: -3.5 }}>
                  <JobLocationIcon location={selectedJob.origin} />
                </Box>
              )
            }
          />
          <JobInfoRow
            label={'Distance'}
            value={
              selectedJob.startTime && selectedJob.endTime
                ? `${formatDuration(
                    new Date(selectedJob.startTime),
                    new Date(selectedJob.endTime),
                    'hh:mm'
                  )}${
                    selectedJob.estimatedDuration
                      ? ` • ${Math.round(
                          selectedJob.estimatedDuration.distanceKilometers
                        )}km`
                      : ''
                  }`
                : '-'
            }
            icon={<IconMapRoute stroke={1.5} size={20} color="#1A1A1A" />}
          />
          <JobInfoRow
            label={'Duration'}
            value={
              selectedJob.startTime && selectedJob.endTime
                ? `${formatDate(
                    selectedJob.startTime,
                    'HH:mm'
                  ).toLowerCase()} - ${formatDate(
                    selectedJob.endTime,
                    'HH:mm'
                  ).toLowerCase()}`
                : '-'
            }
            icon={<IconClockHour5 stroke={1.5} size={20} color="#1A1A1A" />}
          />
        </Stack>
        {/*
          MARK: Actions
        */}
        {isJobFromPool || jobs.length > 1 ? (
          <Stack
            direction="row"
            mt={4}
            justifyContent="space-between"
            alignItems="center"
            gap={4}
          >
            {jobs.length > 1 && (
              <>
                <Stack direction="row">
                  <IconButton
                    size="small"
                    onClick={() =>
                      setSelectedIndex((index) =>
                        index - 1 >= 0 ? index - 1 : jobs.length - 1
                      )
                    }
                    sx={(theme) => ({
                      borderRadius: 0,
                      borderTopLeftRadius: 4,
                      borderBottomLeftRadius: 4,
                      border: `1px solid ${theme.palette.grey[200]}`,
                      padding: 1.2,
                    })}
                  >
                    <IconChevronLeft stroke={1.5} size={24} color="#1A1A1A" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setSelectedIndex((index) => (index + 1) % jobs.length)
                    }
                    sx={(theme) => ({
                      borderRadius: 0,
                      borderTopRightRadius: 4,
                      borderBottomRightRadius: 4,
                      border: `1px solid ${theme.palette.grey[200]}`,
                      borderLeft: 'none',
                      padding: 1.2,
                    })}
                  >
                    <IconChevronRight stroke={1.5} size={24} color="#1A1A1A" />
                  </IconButton>
                </Stack>
                <IconButton
                  size="small"
                  onClick={() => {
                    onShowOnMap(clusterId as number);
                  }}
                  sx={(theme) => ({
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.grey[200]}`,
                    padding: 1.2,
                  })}
                >
                  <ZoomIcon />
                </IconButton>
              </>
            )}
            {isJobFromPool && (
              <Button
                onClick={() => {
                  onAddToTour?.(selectedJob);
                }}
                size="medium"
                variant="contained"
              >
                {t('addToTour')}
              </Button>
            )}
          </Stack>
        ) : null}
      </Stack>
    </div>
  );
};

const JobInfoRow = ({
  label,
  icon,
  value,
}: {
  label: string;
  icon: React.ReactNode;
  value: string;
}) => {
  const theme = useTheme();
  return (
    <Stack
      justifyContent="space-between"
      flexDirection="row"
      alignItems="center"
    >
      <Typography>{label}</Typography>
      <Stack width="50%" alignItems="center" gap={2.5} flexDirection="row">
        {icon}
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore */}
        <Typography color={theme.palette.dark[600]}>{value}</Typography>
      </Stack>
    </Stack>
  );
};

const ZoomIcon = () => {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M18.9286 10.5709C18.9286 6.42873 15.5708 3.07087 11.4286 3.07087C7.28651 3.07087 3.92864 6.42873 3.92864 10.5709C3.92864 14.713 7.2865 18.0709 11.4286 18.0709C15.5708 18.0709 18.9286 14.713 18.9286 10.5709ZM11.4286 1.78516C16.2809 1.78516 20.2144 5.71865 20.2144 10.5709C20.2144 15.4231 16.2809 19.3566 11.4286 19.3566C9.23392 19.3566 7.22715 18.5518 5.68732 17.2213L2.88321 20.0254C2.63216 20.2765 2.22513 20.2765 1.97407 20.0254C1.72302 19.7744 1.72302 19.3674 1.97407 19.1163L4.77819 16.3122C3.44768 14.7724 2.64293 12.7656 2.64293 10.5709C2.64293 5.71865 6.57643 1.78516 11.4286 1.78516ZM14.2144 10.5709C14.2144 10.9259 13.9265 11.2137 13.5715 11.2137L12.0715 11.2137L12.0715 12.7137C12.0715 13.0688 11.7837 13.3566 11.4286 13.3566C11.0736 13.3566 10.7858 13.0688 10.7858 12.7137L10.7858 11.2137L9.28578 11.2137C8.93074 11.2137 8.64293 10.9259 8.64293 10.5709C8.64293 10.2158 8.93074 9.92801 9.28578 9.92801L10.7858 9.92801L10.7858 8.42801C10.7858 8.07297 11.0736 7.78516 11.4286 7.78516C11.7837 7.78516 12.0715 8.07297 12.0715 8.42801L12.0715 9.92801L13.5715 9.92801C13.9265 9.92801 14.2144 10.2158 14.2144 10.5709Z"
        fill="black"
        fillOpacity="0.6"
      />
    </svg>
  );
};
